import { getConditionLabel } from "@features/ax/utils";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Activity } from "lucide-react";
import React from "react";

interface NetworkKeyStatsCardProps {
  data: {
    age: number;
    conditionScore: number;
    riskScore: number;
    efficiency: number;
    riskStatus: string;
  };
}

const getScoreColor = (score: number) => {
  if (score >= 80) return "text-green-600";
  if (score >= 60) return "text-orange-600";
  return "text-red-600";
};

const getScoreLabel = (score: number) => {
  if (score >= 80) return "Good";
  if (score >= 60) return "Fair";
  return "Poor";
};

export const NetworkKeyStatsCard: React.FC<NetworkKeyStatsCardProps> = ({
  data,
}) => {
  const { label, color } = getConditionLabel(data?.conditionScore);

  return (
    <Card className="">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-green-600" />
          <CardTitle className="text-lg font-semibold">Key Stats</CardTitle>
        </div>
        <p className="text-sm text-gray-600">
          Performance indicators and health metrics
        </p>
      </CardHeader>
      <CardContent className="space-y-3 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Age</span>
            <CustomTooltip message="The age of the network since its commission date." />
          </div>
          <span className="font-semibold">{data.age} years</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Condition Score</span>
            <CustomTooltip message="The overall physical health of the network, based on the aggregated condition of its assets." />
          </div>
          <div className="text-right">
            <span className={`font-semibold ${color}`}>
              {data.conditionScore}
            </span>
            <span className={`text-sm ml-2 ${color}`}>{label}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Risk Score</span>
            <CustomTooltip message="The overall risk this network poses, based on its criticality assessment." />
          </div>
          <div className="text-right">
            <span className={`font-semibold ${getScoreColor(data.riskScore)}`}>
              {data.riskScore}
            </span>
            <span className={`text-sm ml-2 ${getScoreColor(data.riskScore)}`}>
              {data.riskStatus}
            </span>
          </div>
        </div>

        {/* <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Efficiency</span>
            <CustomTooltip message="The percentage of water supplied to the network that is successfully delivered to customers, accounting for losses like leaks." />
          </div>
          <span className="font-semibold text-green-600">
            {data.efficiency}%
          </span>
        </div> */}
      </CardContent>
    </Card>
  );
};
